/**
 * ControlConfigPanel - Progressive disclosure configuration for AI_01
 *
 * Level 1: Simple enable/disable toggle with status
 * Level 2: Advanced options (scanning mode, PII types, thresholds)
 * Level 3: Help panel with implementation paths
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Button, Collapse, Tag, Alert, Spin } from 'antd';
import {
  SettingOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';
import InstrumentationGuideModal from './InstrumentationGuideModal';
import PIITestModal from './PIITestModal';
import BatchScanModal from './BatchScanModal';
import BasicConfigSection from './config/BasicConfigSection';
import PIIDetectionSection from './config/PIIDetectionSection';
import CustomPIISection from './config/CustomPIISection';
import PerformanceSection from './config/PerformanceSection';

const { Panel } = Collapse;

function ControlConfigPanel({ projectId, controlCode, project }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // UI state
  const [guideModalOpen, setGuideModalOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [batchScanModalOpen, setBatchScanModalOpen] = useState(false);
  const [activeKeys, setActiveKeys] = useState(['1']); // Default to level 1 open
  const [customPIIForm, setCustomPIIForm] = useState({
    name: '',
    regex: '',
    severity: 'medium',
    base_confidence: 70,
    context_labels: '',
    examples: ''
  });
  const [editingCustomPII, setEditingCustomPII] = useState(null); // Index of custom PII being edited

  useEffect(() => {
    if (projectId && controlCode) {
      fetchConfig();
    }
  }, [projectId, controlCode]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getControlConfig(projectId, controlCode);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
      // Set default config if fetch fails
      setConfig({
        enabled: false,
        pii_scanning_mode: 'none',
        pii_types_enabled: [],
        implementation_path: 'scanner',
        confidence_threshold: 80.0,
        max_doc_size_kb: 256,
        sampling_rate: 0.1,
        custom_pii_types: null,
        suppression_rules: null
      });
      // Only show error if it's not a network/404 error
      if (error.response && error.response.status !== 404) {
        showError('Failed to load configuration. Using defaults.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveControlConfig(projectId, controlCode, config);
      showSuccess('Configuration saved successfully');
      setHasChanges(false);
      fetchConfig(); // Refresh to get server state
    } catch (error) {
      console.error('Failed to save config:', error);
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig({ ...config, [field]: value });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!config) return null;

  // Calculate status
  const isConfigured = config.enabled && config.pii_scanning_mode && config.pii_scanning_mode !== 'none';
  const statusIcon = isConfigured ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <WarningOutlined style={{ color: '#faad14' }} />;
  const statusText = isConfigured ? 'Configured' : 'Setup Required';

  return (
    <>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <SettingOutlined />
            <span>AI_01 Configuration</span>
            {statusIcon}
            <Tag color={isConfigured ? 'green' : 'orange'}>{statusText}</Tag>
          </div>
        }
        extra={
          <div style={{ display: 'flex', gap: '8px', position: 'sticky', top: 0, zIndex: 10, background: 'white', padding: '8px 0' }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => setBatchScanModalOpen(true)}
            >
              Batch Scan
            </Button>
            <Button
              icon={<ExperimentOutlined />}
              onClick={() => setTestModalOpen(true)}
              disabled={!isConfigured}
            >
              Test & Preview
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges}
            >
              Save Changes
            </Button>
          </div>
        }
      >
        <Collapse
          activeKey={activeKeys}
          onChange={setActiveKeys}
          expandIconPosition="right"
        >
          {/* LEVEL 1: Simple Enable/Disable */}
          <Panel
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <strong>1. Enable PII Detection</strong>
                {config.enabled && <Tag color="green">Active</Tag>}
              </div>
            }
            key="1"
          >
            <div style={{ padding: '16px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                <Switch
                  checked={config.enabled}
                  onChange={(checked) => updateConfig('enabled', checked)}
                  size="large"
                />
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {config.enabled ? 'PII Detection Enabled' : 'PII Detection Disabled'}
                  </div>
                  <div style={{ color: '#888', fontSize: '13px' }}>
                    {config.enabled
                      ? 'TrustGrid is monitoring events for Personally Identifiable Information'
                      : 'Enable to start detecting PII in your AI/ML events'}
                  </div>
                </div>
              </div>

              {!config.enabled && (
                <Alert
                  type="info"
                  message="Getting Started"
                  description={
                    <div>
                      <p>To use PII detection, you can either:</p>
                      <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
                        <li><strong>Use TrustGrid's Scanner:</strong> We'll scan event metadata for common PII types</li>
                        <li><strong>Instrument Your Application:</strong> Send PII detection flags with your events</li>
                      </ul>
                      <Button
                        type="link"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideModalOpen(true)}
                        style={{ padding: 0 }}
                      >
                        View Implementation Guide
                      </Button>
                    </div>
                  }
                  showIcon
                  style={{ marginTop: '16px' }}
                />
              )}
            </div>
          </Panel>

          {/* LEVEL 2: Advanced Options */}
          <Panel
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <strong>2. Advanced Configuration</strong>
                {config.pii_scanning_mode && config.pii_scanning_mode !== 'none' && (
                  <Tag color="blue">{config.pii_scanning_mode}</Tag>
                )}
              </div>
            }
            key="2"
            disabled={!config.enabled}
          >
            <div style={{ padding: '16px 0' }}>
              {/* Basic Configuration Section */}
              <BasicConfigSection config={config} updateConfig={updateConfig} />

              {/* PII Detection Section */}
              <PIIDetectionSection config={config} updateConfig={updateConfig} />

              {/* Custom PII Types Section */}
              <CustomPIISection
                config={config}
                updateConfig={updateConfig}
                customPIIForm={customPIIForm}
                setCustomPIIForm={setCustomPIIForm}
                editingCustomPII={editingCustomPII}
                setEditingCustomPII={setEditingCustomPII}
              />

              {/* Performance Settings Section */}
              <PerformanceSection config={config} updateConfig={updateConfig} />
            </div>
          </Panel>

          {/* LEVEL 3: Help & Guidance */}
          <Panel
            header={<strong>3. Implementation Help & Tools</strong>}
            key="3"
          >
            <div style={{ padding: '16px 0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Button
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setGuideModalOpen(true)}
                  block
                >
                  📚 View Implementation Guide (Python, JS, API)
                </Button>
                <Button
                  icon={<ExperimentOutlined />}
                  onClick={() => setTestModalOpen(true)}
                  disabled={!isConfigured}
                  block
                >
                  🧪 Test PII Scanner & Preview Events
                </Button>
                <Button
                  icon={<HistoryOutlined />}
                  onClick={() => {/* Navigate to batch rescan */}}
                  disabled={!isConfigured}
                  block
                >
                  🔄 Batch Re-scan Historical Events
                </Button>
              </div>

              <Alert
                type="info"
                message="Need Help?"
                description={
                  <div>
                    <p><strong>Recommended Approach:</strong></p>
                    <ol style={{ marginTop: '8px', marginLeft: '16px' }}>
                      <li>Enable PII detection with scanner mode (async or hybrid)</li>
                      <li>Test with sample data to verify detection works</li>
                      <li>Monitor coverage in Dashboard (aim for &gt;90%)</li>
                      <li>Optionally add instrumentation for better accuracy</li>
                    </ol>
                  </div>
                }
                showIcon
                style={{ marginTop: '16px' }}
              />
            </div>
          </Panel>
        </Collapse>

        {hasChanges && (
          <Alert
            type="warning"
            message="Unsaved Changes"
            description="You have unsaved configuration changes. Click 'Save Changes' to apply them."
            showIcon
            style={{ marginTop: '16px' }}
          />
        )}
      </Card>

      {/* Modals */}
      <InstrumentationGuideModal
        open={guideModalOpen}
        onClose={() => setGuideModalOpen(false)}
        projectId={projectId}
        project={project}
      />

      <PIITestModal
        open={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        projectId={projectId}
        config={config}
      />

      <BatchScanModal
        visible={batchScanModalOpen}
        onClose={() => setBatchScanModalOpen(false)}
        projectId={projectId}
      />
    </>
  );
}

export default ControlConfigPanel;
